import { FORCE_RULES, RM_COHORTS, isRmCohort, type ForceRole, type RmCohort, type RMRecord } from '../shared/q3';

export type Status = 'Qualified' | 'Mandatory gap' | 'In the race';

/** An RM placed on a cohort board, with everything the views need precomputed. */
/** One product's business for an RM, in ₹ Cr (negative = net redemption). */
export interface ProductLine {
  key: string;
  label: string;
  cr: number;
}

/** The framework's product buckets, in the order the MIS lists them. */
const CORE_PRODUCTS = [
  ['mf', 'Mutual Funds'],
  ['sif', 'SIF'],
  ['assets', 'Assets'],
  ['offshore', 'Offshore'],
  ['pms', 'PMS'],
  ['aifCat3', 'AIF Cat III'],
  ['demat', 'Demat'],
  ['bonds', 'Bonds'],
] as const;
const MANDATORY_PRODUCTS = [
  ['aifCat2', 'AIF Cat II'],
  ['ncd', 'NCD'],
  ['corpFd', 'Corporate FD'],
] as const;

const lines = (r: RMRecord, list: ReadonlyArray<readonly [keyof RMRecord, string]>): ProductLine[] =>
  list.map(([key, label]) => ({ key, label, cr: Number(r[key]) || 0 }));

export interface Standing {
  empId: string;
  rm: string;
  head: string;
  loc: string;
  cohort: RmCohort;
  /** Business by product, for the product-wise split. */
  products: { core: ProductLine[]; mandatory: ProductLine[] };
  coreCr: number;
  cat2Cr: number;
  totalCr: number;
  targetCr: number;
  minCr: number;
  pct: number;
  cat2Pct: number;
  qualified: boolean;
  status: Status;
  totalGapCr: number;
  cat2GapCr: number;
  /** Amount still needed to qualify: the larger of the total and Mandatory shortfalls. */
  gapCr: number;
  rank: number;
  cohortSize: number;
  /** False until the RM has any business: unranked rows show "–" and stay off the podium. */
  ranked: boolean;
  /** How many others share this rank. */
  tied: number;
  /** Nearest RM strictly ahead, their rank, and the business it takes to draw level. */
  chaseName: string;
  chaseRank: number;
  chaseCr: number;
}

export interface Summary {
  cohort: RmCohort | 'All';
  rms: number;
  totalCr: number;
  targetCr: number;
  toGoCr: number;
  progress: number;
  coreCr: number;
  cat2Cr: number;
  cat2Share: number;
  qualified: number;
  qualifiedShare: number;
  cat2Gap: number;
  halfway: number;
  underHalf: number;
}

export interface Team {
  head: string;
  rms: number;
  qualified: number;
  totalCr: number;
  targetCr: number;
  progress: number;
}

interface Rankable {
  rm: string;
  qualified: boolean;
  pct: number;
  rank: number;
  cohortSize: number;
  ranked: boolean;
  tied: number;
  chaseName: string;
  chaseRank: number;
}

/**
 * Shared ("1, 2, 2, 4") ranks over an already-sorted board: equal scores share
 * a rank instead of being split by sheet order. The chase target is the nearest
 * person strictly ahead. Anyone with no business yet is marked unranked.
 */
function applyRanks<T extends Rankable>(sorted: T[], setChase: (r: T, up: T | null) => void): void {
  const same = (a: T, b: T) => a.qualified === b.qualified && Math.abs(a.pct - b.pct) < 1e-9;
  sorted.forEach((r, i) => {
    r.rank = i > 0 && same(sorted[i - 1], r) ? sorted[i - 1].rank : i + 1;
    r.cohortSize = sorted.length;
    r.ranked = r.qualified || r.pct > 0;
  });
  const perRank = new Map<number, number>();
  for (const r of sorted) perRank.set(r.rank, (perRank.get(r.rank) ?? 0) + 1);
  sorted.forEach((r, i) => {
    r.tied = (perRank.get(r.rank) ?? 1) - 1;
    let j = i - 1;
    while (j >= 0 && sorted[j].rank === r.rank) j--;
    const up = j >= 0 ? sorted[j] : null;
    r.chaseName = up ? up.rm : '';
    r.chaseRank = up ? up.rank : 0;
    setChase(r, up);
  });
}

/** RMs without a cohort are left off every board until the sheet assigns one. */
export function computeStandings(records: RMRecord[]): Standing[] {
  const rows: Standing[] = records
    .filter((r): r is RMRecord & { cohort: RmCohort } => isRmCohort(r.cohort))
    .map((r) => {
      const qualified = r.qual === 'Qualified';
      const totalGapCr = Math.max(r.targetCr - r.totalCr, 0);
      const cat2GapCr = Math.max(r.minCr - r.cat2Total, 0);
      return {
        empId: r.empId,
        rm: r.rm,
        head: r.head,
        loc: r.loc,
        cohort: r.cohort,
        products: { core: lines(r, CORE_PRODUCTS), mandatory: lines(r, MANDATORY_PRODUCTS) },
        coreCr: r.coreTotal,
        cat2Cr: r.cat2Total,
        totalCr: r.totalCr,
        targetCr: r.targetCr,
        minCr: r.minCr,
        pct: r.pct,
        cat2Pct: r.minCr > 0 ? r.cat2Total / r.minCr : 0,
        qualified,
        status: qualified ? 'Qualified' : r.totalCr >= r.targetCr ? 'Mandatory gap' : 'In the race',
        totalGapCr,
        cat2GapCr,
        gapCr: Math.max(totalGapCr, cat2GapCr),
        rank: 0,
        cohortSize: 0,
        ranked: false,
        tied: 0,
        chaseName: '',
        chaseRank: 0,
        chaseCr: 0,
      };
    });

  // Qualified RMs rank first, then by % of target.
  const order = (a: Standing, b: Standing) => Number(b.qualified) - Number(a.qualified) || b.pct - a.pct;
  for (const cohort of RM_COHORTS) {
    const group = rows.filter((r) => r.cohort === cohort).sort(order);
    applyRanks(group, (r, up) => {
      r.chaseCr = up ? Math.max((up.pct - r.pct) * r.targetCr, 0) : 0;
    });
  }
  return rows;
}

export function computeSummary(standings: Standing[]): Summary[] {
  return [...RM_COHORTS, 'All' as const].map((cohort) => {
    const g = standings.filter((r) => cohort === 'All' || r.cohort === cohort);
    const sum = (f: (r: Standing) => number) => g.reduce((s, r) => s + f(r), 0);
    const count = (f: (r: Standing) => boolean) => g.filter(f).length;
    const totalCr = sum((r) => r.totalCr);
    const targetCr = sum((r) => r.targetCr);
    const cat2Cr = sum((r) => r.cat2Cr);
    const qualified = count((r) => r.qualified);
    return {
      cohort,
      rms: g.length,
      totalCr,
      targetCr,
      toGoCr: Math.max(targetCr - totalCr, 0),
      progress: targetCr ? totalCr / targetCr : 0,
      coreCr: sum((r) => r.coreCr),
      cat2Cr,
      cat2Share: totalCr ? cat2Cr / totalCr : 0,
      qualified,
      qualifiedShare: g.length ? qualified / g.length : 0,
      cat2Gap: count((r) => r.status === 'Mandatory gap'),
      halfway: count((r) => r.status === 'In the race' && r.pct >= 0.5),
      underHalf: count((r) => r.status === 'In the race' && r.pct < 0.5),
    };
  });
}

export function computeTeams(standings: Standing[]): Team[] {
  const heads = [...new Set(standings.map((r) => r.head))];
  return heads
    .map((head) => {
      const g = standings.filter((r) => r.head === head);
      const totalCr = g.reduce((s, r) => s + r.totalCr, 0);
      const targetCr = g.reduce((s, r) => s + r.targetCr, 0);
      return {
        head,
        rms: g.length,
        qualified: g.filter((r) => r.qualified).length,
        totalCr,
        targetCr,
        progress: targetCr ? totalCr / targetCr : 0,
      };
    })
    .sort((a, b) => b.progress - a.progress);
}

/** A Star Force member on its board. */
export interface ForceStanding {
  empId: string;
  rm: string;
  role: ForceRole;
  loc: string;
  cohort: 'Star Force';
  /** Achieved % from the MIS (0.62 = 62%). */
  achieved: number;
  /** Progress to qualifying: achieved ÷ the 50% bar (1 = qualified). Drives ranking, bars and pace. */
  pct: number;
  qualified: boolean;
  status: 'Qualified' | 'In the race';
  progressLabel: string;
  needLabel: string;
  rank: number;
  cohortSize: number;
  ranked: boolean;
  tied: number;
  chaseName: string;
  chaseRank: number;
  /** Achieved-% points to draw level with the person ahead (0.05 = 5 pts). */
  chasePct: number;
}

export interface ForceSummary {
  group: 'All' | ForceRole;
  members: number;
  qualified: number;
  halfway: number;
  underHalf: number;
  avgAchieved: number;
}

const P = (x: number) => Math.round(x * 100) + '%';

export function computeForce(records: RMRecord[]): ForceStanding[] {
  const bar = FORCE_RULES.qualifyAt;
  const rows = records
    .filter((r): r is RMRecord & { role: ForceRole } => r.cohort === 'Star Force' && r.role !== null)
    .map((m): ForceStanding => {
      const achieved = m.pct;
      const qualified = m.qual === 'Qualified';
      const gapPts = Math.max(Math.ceil((bar - achieved) * 100), 0);
      return {
        empId: m.empId,
        rm: m.rm,
        role: m.role,
        loc: m.loc,
        cohort: 'Star Force',
        achieved,
        pct: achieved / bar,
        qualified,
        status: qualified ? 'Qualified' : 'In the race',
        progressLabel: P(achieved) + ' achieved',
        needLabel: qualified ? '—' : gapPts + ' pts to ' + P(bar),
        rank: 0,
        cohortSize: 0,
        ranked: false,
        tied: 0,
        chaseName: '',
        chaseRank: 0,
        chasePct: 0,
      };
    });

  rows.sort((a, b) => Number(b.qualified) - Number(a.qualified) || b.achieved - a.achieved);
  applyRanks(rows, (r, up) => {
    r.chasePct = up ? Math.max(up.achieved - r.achieved, 0) : 0;
  });
  return rows;
}

export function computeForceSummary(force: ForceStanding[]): ForceSummary[] {
  return (['All', 'Wealth Acquisition', 'IC'] as const).map((group) => {
    const g = force.filter((r) => group === 'All' || r.role === group);
    return {
      group,
      members: g.length,
      qualified: g.filter((r) => r.qualified).length,
      halfway: g.filter((r) => !r.qualified && r.pct >= 0.5).length,
      underHalf: g.filter((r) => !r.qualified && r.pct < 0.5).length,
      avgAchieved: g.length ? g.reduce((s, r) => s + r.achieved, 0) / g.length : 0,
    };
  });
}

const normName = (s: string) => s.toLowerCase().replace(/[^a-z\s]/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Match the signed-in Google name to an RM row. Exact (normalised) match
 * first; failing that, a unique first + last name match, so "Vipin Anand"
 * finds "Vipin Yashpal Anand". Returns null when ambiguous.
 */
export function matchByName<T extends { rm: string }>(standings: T[], name: string | undefined): T | null {
  if (!name) return null;
  const want = normName(name);
  if (!want) return null;
  const exact = standings.filter((r) => normName(r.rm) === want);
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return null;
  const parts = want.split(' ');
  if (parts.length < 2) return null;
  const [first, last] = [parts[0], parts[parts.length - 1]];
  const loose = standings.filter((r) => {
    const p = normName(r.rm).split(' ');
    return p[0] === first && p[p.length - 1] === last;
  });
  return loose.length === 1 ? loose[0] : null;
}
